import { useState, useCallback, useRef, useEffect } from 'react';
import {
  FileInfo,
  FileUploadProps,
  FileUploadState,
  FileValidationResult,
} from './types';

export const useFileUpload = (props: FileUploadProps) => {
  const {
    acceptedFormats = ['.txt', '.xml'],
    maxFiles = 200, // Just in case (user should upload as many as he needs)
    maxFileSize = 50 * 1024 * 1024, // 50MB default
    value,
    onFilesChange,
    multiple = true,
    disabled = false,
  } = props;

  const [state, setState] = useState<FileUploadState>({
    files: [],
    isDragOver: false,
    errors: [],
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync internal state with external value prop when provided
  useEffect(() => {
    if (value !== undefined) {
      setState(prev => ({ ...prev, files: value }));
    }
  }, [value]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const generateFileId = (): string => {
    return `file_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  };

  const validateFile = useCallback(
    (file: File): FileValidationResult => {
      if (file.size > maxFileSize) {
        return {
          isValid: false,
          error: `File size exceeds limit of ${formatFileSize(maxFileSize)}`,
        };
      }

      return { isValid: true };
    },
    [acceptedFormats, maxFileSize]
  );

  const createFileInfo = useCallback(
    (file: File): FileInfo => ({
      file,
      id: generateFileId(),
      name: file.name,
      size: formatFileSize(file.size),
      type: file.type || 'Unknown',
    }),
    []
  );

  const addFiles = useCallback(
    (newFiles: File[]) => {
      if (disabled) return;

      const validFiles: FileInfo[] = [];
      const errors: string[] = [];

      for (const file of newFiles) {
        const validation = validateFile(file);

        if (!validation.isValid) {
          errors.push(`${file.name}: ${validation.error}`);
          continue;
        }

        if (!multiple && state.files.length > 0) {
          setState(prev => ({
            ...prev,
            files: [createFileInfo(file)],
            errors: [],
          }));
          onFilesChange([createFileInfo(file)]);
          return;
        }

        if (
          maxFiles !== Infinity &&
          state.files.length + validFiles.length >= maxFiles
        ) {
          errors.push(`Maximum ${maxFiles} files allowed`);
          break;
        }

        const isDuplicate = state.files.some(
          existingFile =>
            existingFile.name === file.name &&
            existingFile.size === formatFileSize(file.size)
        );

        if (isDuplicate) {
          errors.push(`${file.name}: File already selected`);
          continue;
        }

        validFiles.push(createFileInfo(file));
      }

      if (validFiles.length > 0 || errors.length > 0) {
        const updatedFiles = multiple
          ? [...state.files, ...validFiles]
          : validFiles;
        setState(prev => ({
          ...prev,
          files: updatedFiles,
          errors,
          isDragOver: false,
        }));
        onFilesChange(updatedFiles);
      }
    },
    [
      state.files,
      maxFiles,
      multiple,
      disabled,
      onFilesChange,
      createFileInfo,
      validateFile,
    ]
  );

  const removeFile = useCallback(
    (fileId: string) => {
      if (disabled) return;

      const updatedFiles = state.files.filter(file => file.id !== fileId);
      setState(prev => ({ ...prev, files: updatedFiles, errors: [] }));
      onFilesChange(updatedFiles);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [state.files, disabled, onFilesChange]
  );

  const clearAllFiles = useCallback(() => {
    if (disabled) return;

    setState(prev => ({ ...prev, files: [], errors: [] }));
    onFilesChange([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [disabled, onFilesChange]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      setState(prev => ({ ...prev, isDragOver: true }));
    },
    [disabled]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      setState(prev => ({ ...prev, isDragOver: false }));
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      setState(prev => ({ ...prev, isDragOver: false }));

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        addFiles(files);
      }
    },
    [addFiles, disabled]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length > 0) {
        addFiles(files);
      }
      // Reset input value immediately to allow re-selecting same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [addFiles, disabled]
  );

  const handleClickUploadArea = useCallback(() => {
    if (disabled) return;
    fileInputRef.current?.click();
  }, [disabled]);

  const clearErrors = useCallback(() => {
    setState(prev => ({ ...prev, errors: [] }));
  }, []);

  return {
    files: state.files,
    isDragOver: state.isDragOver,
    errors: state.errors,
    fileInputRef,
    acceptedFormats,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleInputChange,
    handleClickUploadArea,
    removeFile,
    clearAllFiles,
    clearErrors,
    disabled,
  };
};
